import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/AuthProvider';
import Spinner from '../../components/spinner';
import ThemeToggle from '../../components/ThemeToggle';

const OAuthLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get('client_id');
  const redirectUri = queryParams.get('redirect_uri');
  const responseType = queryParams.get('response_type');
  const scope = queryParams.get('scope');
  const state = queryParams.get('state');

  // Check if we have all required OAuth parameters
  const hasAllParams = clientId && redirectUri && responseType;

  // Handle login form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(username, password);
      
      // Update auth context with user info and token
      login(data.user, data.token);
      
      // After successful login, redirect to consent page
      if (hasAllParams) {
        // Preserve all OAuth query parameters for the consent page
        const consentParams = new URLSearchParams();
        if (clientId) consentParams.append('client_id', clientId);
        if (redirectUri) consentParams.append('redirect_uri', redirectUri);
        if (responseType) consentParams.append('response_type', responseType);
        if (scope) consentParams.append('scope', scope);
        if (state) consentParams.append('state', state);
        
        navigate(`/oauth/consent?${consentParams.toString()}`);
      } else {
        // If missing OAuth params, just go to the dashboard
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.error || 'Failed to log in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <ThemeToggle compact className="fixed right-4 top-4" />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-50">
          Zafron
        </h2>
        {hasAllParams && (
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            You need to sign in to authorize the application.
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-900 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-400 p-4 mb-4">
              <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="email"
                  autoComplete="email"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-800 dark:text-gray-50 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-800 dark:text-gray-50 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-indigo-500"
              >
                {loading ? <Spinner size="sm" /> : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OAuthLogin;